import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { CenterTd, LeftTd } from 'styles';
import { design, formatTime, getGameStars, getWinner } from 'utils';
import starAway from 'assets/starAway.svg';
import starHome from 'assets/starHome.svg';

const ScheduleTbody = styled.tbody`
  tr td {
    padding: ${design.spacing[2]};
  }
  .winner {
    font-weight: bold;
  }
  .homeTeam {
    text-align: right;
  }
`;
const ScheduleTh = styled.th`
  ${(props) =>
    props.$isPlayoffs &&
    `
    background-color: ${design.colors.gbhpcHome} !important;
  `}
  ${(props) =>
    props.$isChampionship &&
    `
    background-color: ${design.colors.gold} !important;
  `}
  ${(props) =>
    props.$isByeWeek &&
    `
    background-color: ${design.colors.gray[5]} !important;
  `}
`;
const GameRow = styled.tr`
  ${(props) =>
    props.$cancel &&
    `
    &,
    a {
      color: ${design.colors.gray[5]};
      font-style: italic;
    }
    .awayTeam,
    .homeTeam {
      text-decoration: line-through;
    }
  `}
`;
const ResultTd = styled.td`
  ${(props) =>
    props.$cancel &&
    `
  font-weight: bold;
  `}
`;
const PickupCell = styled.td`
  text-align: center;
`;
const ExtraStatsP = styled.p`
  align-items: center;
  display: flex;
  font-size: ${design.fontSizes[1]};
  font-weight: normal;
  margin-top: ${design.spacing[1]};
  ${(props) =>
    props.$home &&
    `
  justify-content: flex-end;`}
`;
const Shots = styled.p`
  text-align: center;
  font-size: ${design.fontSizes[1]};
  font-weight: normal;
  margin-top: ${design.spacing[1]};
`;
const GameStarStats = styled.span`
  font-variant-caps: small-caps;
  margin-left: ${design.spacing[1]};
`;

const ScheduleBody = ({ date = '', games, inlineDate, seasonId, showEdit }) => {
  const isPlayoffs = games.some((game) => game.playoffs);
  const isChampionship = games.filter((game) => game.playoffs).length === 1;
  const isByeWeek = games.every((game) => game.cancel);
  const note = games[0].note;

  return (
    <ScheduleTbody>
      {!inlineDate && (
        <tr>
          <ScheduleTh
            colSpan={showEdit ? 10 : 9}
            $isByeWeek={isByeWeek}
            $isChampionship={isChampionship}
            $isPlayoffs={isPlayoffs}
          >
            {format(new Date(`${date}T20:00:00.000-05:00`), 'EEEE MMMM d yyyy')}
            {isPlayoffs && !isChampionship ? ' - Playoffs' : ''}
            {isChampionship ? ' - Championship' : ''}
            {note ? ` - ${note}` : ''}
          </ScheduleTh>
        </tr>
      )}
      {games.map((game) => {
        const {
          id,
          awayScore,
          awayShots,
          awayTeam,
          cancel,
          homeScore,
          homeShots,
          homeTeam,
          pickup,
          playoffs,
          ot,
          rinkLocation: { id: rinkId, name: rinkName },
          time,
        } = game;

        const awayTeamName = awayTeam?.name || 'TBD';
        const awayTeamId = awayTeam?.id;
        const homeTeamName = homeTeam?.name || 'TBD';
        const homeTeamId = homeTeam?.id;

        const { awayWinner, homeWinner } = getWinner({ awayScore, homeScore });
        const gameCompleted = homeShots > 0 && awayShots > 0;
        let gameStars;
        if (gameCompleted) {
          gameStars = getGameStars(game);
        }
        return (
          <Fragment key={id}>
            <GameRow $cancel={cancel}>
              {inlineDate && (
                <LeftTd>
                  {game.date}
                  {playoffs && ' *'}
                </LeftTd>
              )}
              <LeftTd>{formatTime(time)}</LeftTd>
              {pickup ? (
                <PickupCell colSpan={7}>Pickup Skate</PickupCell>
              ) : (
                <>
                  <LeftTd
                    className={homeWinner ? 'winner homeTeam' : 'homeTeam'}
                  >
                    {homeTeamId ? (
                      <a href={`/team/${seasonId}/${homeTeamId}`}>
                        {homeTeamName}
                      </a>
                    ) : (
                      homeTeamName
                    )}
                    {gameCompleted && (
                      <ExtraStatsP $home>
                        <img
                          src={starHome}
                          alt="Home Star of the Game"
                          style={{ marginRight: design.spacing[1] }}
                        />
                        <a href={gameStars.homeStar.link}>
                          {gameStars.homeStar.name}
                        </a>
                        :{' '}
                        <GameStarStats>
                          {gameStars.homeStar.stats}
                        </GameStarStats>
                      </ExtraStatsP>
                    )}
                  </LeftTd>
                  <CenterTd className={homeWinner ? 'winner score' : 'score'}>
                    {homeScore}
                    {gameCompleted && <Shots>{homeShots}</Shots>}
                  </CenterTd>
                  <CenterTd>-{gameCompleted && <Shots>Shots</Shots>}</CenterTd>
                  <CenterTd className={awayWinner ? 'winner score' : 'score'}>
                    {awayScore}
                    {gameCompleted && <Shots>{awayShots}</Shots>}
                  </CenterTd>
                  <LeftTd
                    className={awayWinner ? 'winner awayTeam' : 'awayTeam'}
                  >
                    {awayTeamId ? (
                      <a href={`/team/${seasonId}/${awayTeamId}`}>
                        {awayTeamName}
                      </a>
                    ) : (
                      awayTeamName
                    )}
                    {gameCompleted && (
                      <ExtraStatsP>
                        <img
                          src={starAway}
                          alt="Away Star of the Game"
                          style={{ marginRight: design.spacing[1] }}
                        />
                        <a href={gameStars.awayStar.link}>
                          {gameStars.awayStar.name}
                        </a>
                        :{' '}
                        <GameStarStats>
                          {gameStars.awayStar.stats}
                        </GameStarStats>
                      </ExtraStatsP>
                    )}
                  </LeftTd>
                  <td>{ot}</td>
                  <ResultTd $cancel={cancel}>
                    {cancel && 'CANCELED'}
                    {!cancel &&
                      typeof homeScore === 'number' &&
                      typeof awayScore === 'number' && (
                        <Link to={`/game/${id}`}>Game Summary</Link>
                      )}
                  </ResultTd>
                </>
              )}
              <td>
                <Link to={`/rink/${rinkId}`}>{rinkName}</Link>
              </td>
              {showEdit && (
                <td>
                  <Link to={`/admin/game/${id}`}>Edit Score</Link>
                </td>
              )}
            </GameRow>
          </Fragment>
        );
      })}
    </ScheduleTbody>
  );
};

ScheduleBody.propTypes = {
  date: PropTypes.string,
  games: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      awayScore: PropTypes.number,
      awayTeam: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      date: PropTypes.string.isRequired,
      homeScore: PropTypes.number,
      homeTeam: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      ot: PropTypes.string,
      playoffs: PropTypes.bool,
      rinkLocation: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
      time: PropTypes.string.isRequired,
    }),
  ).isRequired,
  inlineDate: PropTypes.bool,
  seasonId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  showEdit: PropTypes.bool,
};

export default ScheduleBody;
